import { useEffect, useState } from "react";
import { Building2, Pencil, Plus, Trash2 } from "lucide-react";

import PilotageEmptyState from "@/components/pilotage/PilotageEmptyState";
import PilotageField from "@/components/pilotage/PilotageField";
import PilotageModal from "@/components/pilotage/PilotageModal";
import PilotageTable from "@/components/pilotage/PilotageTable";
import AppErrorState from "@/components/ui/AppErrorState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/lib/api";
import {
  createClient,
  deleteClient,
  listClients,
  updateClient,
} from "@/lib/pilotageApi";

const EMPTY_FORM = {
  name: "",
  sector: "",
  contact_name: "",
  contact_email: "",
  phone: "",
  address: "",
  siret: "",
  payment_terms_days: "",
};

const EMPTY_CONTACT_FORM = {
  id: "",
  full_name: "",
  email: "",
  phone: "",
  role: "",
  department: "",
  is_primary: false,
  notes: "",
};

const ClientsPage = () => {
  const { role } = useAuth();
  const isReadOnly = role === "RH";

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  
  // Contacts states
  const [contacts, setContacts] = useState([]);
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [editingContactIndex, setEditingContactIndex] = useState(null);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT_FORM);
  const [contactFormError, setContactFormError] = useState("");
  const [contactsTouched, setContactsTouched] = useState(false);

  const showEmptyState = !loading && clients.length === 0;

  const loadClients = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await listClients();
      setClients(data);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Impossible de charger les clients."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadClients();
  }, []);

  const openCreateModal = () => {
    setEditingClient(null);
    setFormValues(EMPTY_FORM);
    setContacts([]);
    setIsAddingContact(false);
    setEditingContactIndex(null);
    setContactForm(EMPTY_CONTACT_FORM);
    setContactFormError("");
    setContactsTouched(false);
    setModalOpen(true);
  };

  const openEditModal = (client) => {
    setEditingClient(client);
    setFormValues({
      name: client.name || "",
      sector: client.sector || "",
      contact_name: client.contact_name || "",
      contact_email: client.contact_email || "",
      phone: client.phone || "",
      address: client.address || "",
      siret: client.siret || "",
      payment_terms_days: client.payment_terms_days !== null && client.payment_terms_days !== undefined ? String(client.payment_terms_days) : "",
    });
    setContacts(client.contacts || []);
    setIsAddingContact(false);
    setEditingContactIndex(null);
    setContactForm(EMPTY_CONTACT_FORM);
    setContactFormError("");
    setContactsTouched(false);
    setModalOpen(true);
  };

  const closeModal = (nextOpen) => {
    setModalOpen(nextOpen);
    if (!nextOpen) {
      setEditingClient(null);
      setFormValues(EMPTY_FORM);
      setContacts([]);
      setIsAddingContact(false);
      setEditingContactIndex(null);
      setContactForm(EMPTY_CONTACT_FORM);
      setContactFormError("");
      setContactsTouched(false);
    }
  };

  const handleSaveContactInline = () => {
    setContactFormError("");
    const name = (contactForm.full_name || "").trim();
    if (!name) {
      setContactFormError("Le nom complet est obligatoire.");
      return;
    }

    const email = (contactForm.email || "").trim();
    if (email) {
      const isEmailDup = contacts.some((c, idx) => {
        if (editingContactIndex !== null && idx === editingContactIndex) {
          return false;
        }
        return c.email?.toLowerCase() === email.toLowerCase();
      });
      if (isEmailDup) {
        setContactFormError("Un contact avec cet e-mail existe déjà.");
        return;
      }
    }

    let updatedContacts = [...contacts];

    if (contactForm.is_primary) {
      updatedContacts = updatedContacts.map((c) => ({ ...c, is_primary: false }));
    }

    const contactData = {
      ...contactForm,
      full_name: name,
      email: email || null,
      phone: (contactForm.phone || "").trim() || null,
      role: (contactForm.role || "").trim() || null,
      department: (contactForm.department || "").trim() || null,
      notes: (contactForm.notes || "").trim() || null,
    };

    if (editingContactIndex !== null) {
      updatedContacts[editingContactIndex] = contactData;
    } else {
      contactData.id = contactData.id || "temp-" + Math.random().toString(36).substr(2, 9);
      updatedContacts.push(contactData);
    }

    const hasPrimary = updatedContacts.some((c) => c.is_primary);
    if (!hasPrimary && updatedContacts.length > 0) {
      updatedContacts[0].is_primary = true;
    }

    setContacts(updatedContacts);
    setContactsTouched(true);
    setIsAddingContact(false);
    setEditingContactIndex(null);
    setContactForm(EMPTY_CONTACT_FORM);
  };

  const handleStartEditContact = (index) => {
    setEditingContactIndex(index);
    setIsAddingContact(false);
    const contact = contacts[index];
    setContactForm({
      ...EMPTY_CONTACT_FORM,
      ...contact,
      full_name: contact.full_name || "",
      email: contact.email || "",
      phone: contact.phone || "",
      role: contact.role || "",
      department: contact.department || "",
      notes: contact.notes || "",
    });
    setContactFormError("");
  };

  const handleDeleteContactInline = (index) => {
    const isDeletedPrimary = contacts[index].is_primary;
    let updatedContacts = contacts.filter((_, idx) => idx !== index);

    if (isDeletedPrimary && updatedContacts.length > 0) {
      updatedContacts[0].is_primary = true;
    }

    setContacts(updatedContacts);
    setContactsTouched(true);
  };

  const handleSetPrimaryContact = (index) => {
    const updatedContacts = contacts.map((c, idx) => ({
      ...c,
      is_primary: idx === index,
    }));
    setContacts(updatedContacts);
    setContactsTouched(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    const primary = contacts.find((c) => c.is_primary) || contacts[0];

    const hasNoContactsAfterEdit = contactsTouched && contacts.length === 0;
    const legacyContactName = primary
      ? primary.full_name
      : (formValues.contact_name.trim() || null);
    const legacyContactEmail = primary
      ? primary.email
      : (formValues.contact_email.trim() || null);
    const legacyPhone = primary
      ? primary.phone
      : (formValues.phone.trim() || null);
    const payload = {
      name: formValues.name.trim(),
      sector: formValues.sector.trim() || null,
      contact_name: hasNoContactsAfterEdit ? null : legacyContactName,
      contact_email: hasNoContactsAfterEdit ? null : legacyContactEmail,
      phone: hasNoContactsAfterEdit ? null : legacyPhone,
      address: formValues.address.trim() || null,
      siret: formValues.siret.trim() || null,
      payment_terms_days: formValues.payment_terms_days !== "" ? parseInt(formValues.payment_terms_days, 10) : null,
      contacts: contacts,
    };
    if (editingClient && !contactsTouched) {
      delete payload.contacts;
    }

    try {
      if (editingClient) {
        await updateClient(editingClient.id, payload);
      } else {
        await createClient(payload);
      }

      closeModal(false);
      await loadClients();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Impossible d'enregistrer le client."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (client) => {
    const shouldDelete = window.confirm(
      `Supprimer le client "${client.name}" ?`,
    );

    if (!shouldDelete) {
      return;
    }

    setError("");

    try {
      await deleteClient(client.id);
      await loadClients();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Impossible de supprimer le client."));
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-white/10 glass-heavy p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Pilotage
            </p>
            <h1 className="mt-2 text-2xl font-heading font-bold text-white">
              Clients
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">
              Référentiel des clients actifs et historiques. Les RH disposent
              d&apos;un accès en lecture seule.
            </p>
          </div>
          {!isReadOnly && !showEmptyState ? (
            <Button
              type="button"
              onClick={openCreateModal}
              className="btn-primary"
              data-testid="create-client-button"
            >
              <Plus className="h-4 w-4" />
              Créer client
            </Button>
          ) : null}
        </div>
      </section>

      {error ? <AppErrorState message={error} /> : null}

      {showEmptyState ? (
        <PilotageEmptyState
          icon={Building2}
          title="Aucun client"
          description="Créez votre premier client pour rattacher ensuite vos missions au bon compte."
          actionLabel="Créer client"
          onAction={openCreateModal}
          isReadOnly={isReadOnly}
          testId="clients-empty-state"
        />
      ) : (
        <PilotageTable
          columns={[
            {
              key: "name",
              label: "Client",
              render: (client) => (
                <div>
                  <p className="font-medium text-white">{client.name}</p>
                </div>
              ),
            },
            {
              key: "sector",
              label: "Secteur",
              render: (client) => client.sector || "—",
            },
            {
              key: "contact_name",
              label: "Contact principal",
              render: (client) => {
                const primary = client.contacts?.find((c) => c.is_primary) || client.contacts?.[0];
                return (
                  <div>
                    <p>{primary?.full_name || "—"}</p>
                    {primary?.email ? (
                      <p className="mt-1 text-xs text-slate-500">
                        {primary.email}
                      </p>
                    ) : null}
                  </div>
                );
              },
            },
            {
              key: "contacts_count",
              label: "Nombre de contacts",
              render: (client) => {
                const count = client.contacts?.length || 0;
                return `${count} contact${count > 1 ? "s" : ""}`;
              },
            },
          ]}
          rows={clients}
          loading={loading}
          emptyMessage="Aucun client enregistré."
          renderActions={
            isReadOnly
              ? null
              : (client) => (
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => openEditModal(client)}
                      className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Modifier
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(client)}
                      className="border-red-500/20 text-red-200 hover:bg-red-500/10 hover:text-red-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Supprimer
                    </Button>
                  </div>
                )
          }
        />
      )}

      {!isReadOnly ? (
        <PilotageModal
          open={modalOpen}
          onOpenChange={closeModal}
          title={editingClient ? "Modifier le client" : "Créer un client"}
          description="Les informations saisies seront immédiatement disponibles dans les formulaires de mission."
          onSubmit={handleSubmit}
          submitLabel={editingClient ? "Enregistrer" : "Créer"}
          isSubmitting={saving}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <PilotageField label="Nom" htmlFor="client-name">
              <Input
                id="client-name"
                value={formValues.name}
                onChange={(event) =>
                  setFormValues((current) => ({ ...current, name: event.target.value }))
                }
                placeholder="Nom du client"
                required
              />
            </PilotageField>
            <PilotageField label="Secteur" htmlFor="client-sector">
              <Input
                id="client-sector"
                value={formValues.sector}
                onChange={(event) =>
                  setFormValues((current) => ({ ...current, sector: event.target.value }))
                }
                placeholder="Conseil, industrie..."
              />
            </PilotageField>
            <PilotageField label="Adresse" htmlFor="client-address">
              <Input
                id="client-address"
                value={formValues.address}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    address: event.target.value,
                  }))
                }
                placeholder="123 rue de Paris..."
              />
            </PilotageField>
            <PilotageField label="SIRET" htmlFor="client-siret">
              <Input
                id="client-siret"
                value={formValues.siret}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    siret: event.target.value,
                  }))
                }
                placeholder="12345678901234"
              />
            </PilotageField>
            <PilotageField label="Délai de paiement (jours)" htmlFor="client-payment-terms">
              <Input
                id="client-payment-terms"
                type="number"
                min="0"
                step="1"
                value={formValues.payment_terms_days}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    payment_terms_days: event.target.value,
                  }))
                }
                placeholder="30"
              />
            </PilotageField>
          </div>

          <div className="mt-6 border-t border-white/10 pt-6">
            <h3 className="text-sm font-semibold text-slate-300">Contacts client</h3>
            
            {editingContactIndex !== null || isAddingContact ? (
              <div className="mt-4 p-4 rounded-2xl border border-white/10 bg-white/[0.02] space-y-4">
                <h4 className="text-xs font-semibold text-lenvia-blue uppercase tracking-wider">
                  {editingContactIndex !== null ? "Modifier le contact" : "Ajouter un contact"}
                </h4>
                
                {contactFormError ? (
                  <p className="text-xs text-red-400">{contactFormError}</p>
                ) : null}
                
                <div className="grid gap-4 md:grid-cols-2">
                  <PilotageField label="Nom complet *" htmlFor="contact-fullname">
                    <Input
                      id="contact-fullname"
                      value={contactForm.full_name}
                      onChange={(e) => setContactForm(c => ({ ...c, full_name: e.target.value }))}
                      placeholder="David S."
                      required
                    />
                  </PilotageField>
                  <PilotageField label="Email" htmlFor="contact-email">
                    <Input
                      id="contact-email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm(c => ({ ...c, email: e.target.value }))}
                      placeholder="david.xxx@client.com"
                    />
                  </PilotageField>
                  <PilotageField label="Téléphone" htmlFor="contact-phone">
                    <Input
                      id="contact-phone"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm(c => ({ ...c, phone: e.target.value }))}
                      placeholder="06..."
                    />
                  </PilotageField>
                  <PilotageField label="Rôle" htmlFor="contact-role">
                    <Input
                      id="contact-role"
                      value={contactForm.role}
                      onChange={(e) => setContactForm(c => ({ ...c, role: e.target.value }))}
                      placeholder="Manager"
                    />
                  </PilotageField>
                  <PilotageField label="Département" htmlFor="contact-dept">
                    <Input
                      id="contact-dept"
                      value={contactForm.department}
                      onChange={(e) => setContactForm(c => ({ ...c, department: e.target.value }))}
                      placeholder="Ma Banque"
                    />
                  </PilotageField>
                  <PilotageField label="Notes" htmlFor="contact-notes">
                    <Input
                      id="contact-notes"
                      value={contactForm.notes}
                      onChange={(e) => setContactForm(c => ({ ...c, notes: e.target.value }))}
                      placeholder="Notes diverses"
                    />
                  </PilotageField>
                  <div className="md:col-span-2 flex items-center gap-2 py-2">
                    <input
                      id="contact-primary"
                      type="checkbox"
                      checked={contactForm.is_primary}
                      onChange={(e) => setContactForm(c => ({ ...c, is_primary: e.target.checked }))}
                      className="rounded border-white/10 bg-slate-950 text-lenvia-blue focus:ring-0 focus:ring-offset-0 h-4 w-4"
                    />
                    <label htmlFor="contact-primary" className="text-sm text-slate-300 cursor-pointer">
                      Définir comme contact principal
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setIsAddingContact(false);
                      setEditingContactIndex(null);
                      setContactForm(EMPTY_CONTACT_FORM);
                      setContactFormError("");
                    }}
                    className="border-white/10 text-slate-300 hover:bg-white/5 hover:text-white"
                  >
                    Annuler
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSaveContactInline}
                    className="btn-primary"
                  >
                    Valider le contact
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {contacts.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">Aucun contact enregistré pour ce client.</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar pr-1">
                    {contacts.map((contact, index) => (
                      <div
                        key={contact.id || index}
                        className={`p-3 rounded-2xl border flex items-center justify-between gap-4 transition-colors ${
                          contact.is_primary
                            ? "border-lenvia-blue/30 bg-lenvia-blue/5"
                            : "border-white/5 bg-white/[0.01]"
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-white">
                              {contact.full_name}
                            </span>
                            {contact.role ? (
                              <span className="text-xs text-slate-400">
                                — {contact.role}
                              </span>
                            ) : null}
                            {contact.department ? (
                              <span className="text-[10px] bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded-md">
                                {contact.department}
                              </span>
                            ) : null}
                            {contact.is_primary ? (
                              <span className="text-[9px] font-bold uppercase tracking-wider bg-lenvia-blue text-white px-2 py-0.5 rounded-full">
                                Principal
                              </span>
                            ) : null}
                          </div>
                          
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-400">
                            {contact.email ? <span>{contact.email}</span> : null}
                            {contact.phone ? <span>Tel: {contact.phone}</span> : null}
                            {contact.notes ? <span className="italic">Note: {contact.notes}</span> : null}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1.5 shrink-0">
                          {!contact.is_primary ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => handleSetPrimaryContact(index)}
                              className="text-[10px] h-7 border-white/10 text-slate-300 hover:bg-white/5"
                              data-testid={`set-primary-${index}`}
                            >
                              Définir principal
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => handleStartEditContact(index)}
                            className="text-[10px] h-7 border-white/10 text-slate-300 hover:bg-white/5"
                            data-testid={`edit-contact-${index}`}
                          >
                            Modifier
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteContactInline(index)}
                            className="text-[10px] h-7 border-red-500/20 text-red-200 hover:bg-red-500/10"
                            data-testid={`delete-contact-${index}`}
                          >
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsAddingContact(true);
                    setEditingContactIndex(null);
                    setContactForm({
                      ...EMPTY_CONTACT_FORM,
                      is_primary: contacts.length === 0,
                    });
                    setContactFormError("");
                  }}
                  className="w-full border-dashed border-white/10 hover:border-white/20 text-slate-300 py-4 rounded-2xl flex items-center justify-center gap-2 bg-transparent"
                  data-testid="add-contact-btn"
                >
                  <Plus className="h-4 w-4" />
                  Ajouter un contact
                </Button>
              </div>
            )}
          </div>
        </PilotageModal>
      ) : null}
    </div>
  );
};

export default ClientsPage;
